/**
 * Impact feel: hit stop and trauma screen shake (story `q-combat-game-feel`; design in
 * docs/art/vfx-specs/README.md §Impact feel). A `gameFeedback` subscriber, like the VFX director
 * and the SFX engine: it changes how a blow LOOKS, never game state.
 *
 * - **Hit stop.** A landed blow the own avatar dealt or took pauses the attacker's and the
 *   target's animation for {@link HIT_STOP_MS} ({@link HIT_STOP_HEAVY_MS} when `heavy`). The wire
 *   carries no damage magnitude, so `heavy` is the only scale. Only the two mixers pause:
 *   movement, VFX, UI and input keep running, and the server never waits.
 * - **Screen shake.** Hits on the own avatar and the own heavy blows add trauma (0..1), which
 *   decays at {@link TRAUMA_DECAY_PER_S}. The camera offset is
 *   `SHAKE_MAX_OFFSET_M × trauma² × smooth noise` (squared, so small knocks barely move it).
 *   The noise is a sum of incommensurate sines: smooth, deterministic, no `Math.random`.
 *
 * Every number here is a first playtest value, named so a playtest can move it.
 * Pure TypeScript: no `three`, no DOM — the caller passes the clock and its gates.
 */

import { vfxLevel } from '../vfx/settings.js';
import type { GameFeedbackEvent } from './feedbackBus.js';

/** Hit stop for an ordinary landed blow (ms; cheatsheet range 50–100). */
export const HIT_STOP_MS = 55;
/** Hit stop for a heavy blow (an ability or heavy swing). */
export const HIT_STOP_HEAVY_MS = 95;

/** Trauma added when a blow lands on the own avatar. */
export const TRAUMA_PLAYER_DAMAGED = 0.45;
/** Trauma added when the own avatar lands a heavy blow (ordinary swings add none). */
export const TRAUMA_OWN_HEAVY_HIT = 0.25;
/** Trauma lost per second. */
export const TRAUMA_DECAY_PER_S = 1.4;
/** Camera offset at full trauma (metres) — the follow camera sits a few metres back. */
export const SHAKE_MAX_OFFSET_M = 0.14;

export interface ShakeOffset {
  x: number;
  y: number;
  z: number;
}

const ZERO: ShakeOffset = Object.freeze({ x: 0, y: 0, z: 0 });

/** Smooth noise in [-1, 1] for axis `seed` at time `t` seconds. */
export function smoothNoise(t: number, seed: number): number {
  return (
    (Math.sin(t * 23.1 + seed * 1.7) * 0.5 +
      Math.sin(t * 37.7 + seed * 4.3) * 0.3 +
      Math.sin(t * 61.3 + seed * 9.1) * 0.2) /
    1.0
  );
}

export class ImpactFeel {
  private trauma = 0;
  /** entity id → the time (ms) its animation resumes. */
  private readonly heldUntil = new Map<number, number>();

  /**
   * React to one feedback event. `ownId` is the own avatar's entity id (or < 0 before it is
   * known); `nowMs` the frame clock.
   */
  onFeedback(event: GameFeedbackEvent, ownId: number, nowMs: number): void {
    if (event.kind === 'weaponHit') {
      const own = ownId >= 0 && (event.sourceId === ownId || event.targetId === ownId);
      if (!own) return;
      const until = nowMs + (event.heavy ? HIT_STOP_HEAVY_MS : HIT_STOP_MS);
      this.hold(event.sourceId, until);
      this.hold(event.targetId, until);
      if (event.heavy && event.sourceId === ownId) this.addTrauma(TRAUMA_OWN_HEAVY_HIT);
    } else if (event.kind === 'playerDamaged') {
      this.addTrauma(TRAUMA_PLAYER_DAMAGED);
    }
  }

  /** Whether `entityId`'s animation is paused at `nowMs`. */
  isHeld(entityId: number, nowMs: number): boolean {
    const until = this.heldUntil.get(entityId);
    if (until === undefined) return false;
    if (nowMs < until) return true;
    this.heldUntil.delete(entityId);
    return false;
  }

  /** Any entity paused at `nowMs` (lets the caller skip the per-entity check). */
  anyHeld(nowMs: number): boolean {
    for (const id of [...this.heldUntil.keys()]) if (this.isHeld(id, nowMs)) return true;
    return false;
  }

  /** Current trauma, 0..1 (diagnostics / tests). */
  traumaLevel(): number {
    return this.trauma;
  }

  /**
   * Advance the decay by `dtMs` and return this frame's camera offset. `enabled` false (reduce
   * motion, VFX below Full, capture or an automated lane) returns zero and drops the trauma, so a
   * shake never resumes when the gate opens again.
   */
  shake(nowMs: number, dtMs: number, enabled: boolean): ShakeOffset {
    if (!enabled) {
      this.trauma = 0;
      return ZERO;
    }
    if (this.trauma <= 0) return ZERO;
    this.trauma = Math.max(0, this.trauma - (TRAUMA_DECAY_PER_S * Math.max(0, dtMs)) / 1000);
    const amp = SHAKE_MAX_OFFSET_M * this.trauma * this.trauma;
    if (amp === 0) return ZERO;
    const t = nowMs / 1000;
    return { x: amp * smoothNoise(t, 1), y: amp * smoothNoise(t, 2), z: amp * smoothNoise(t, 3) };
  }

  /** Forget everything (zone swap, teardown). */
  reset(): void {
    this.trauma = 0;
    this.heldUntil.clear();
  }

  private hold(entityId: number, untilMs: number): void {
    const prior = this.heldUntil.get(entityId) ?? 0;
    if (untilMs > prior) this.heldUntil.set(entityId, untilMs);
  }

  private addTrauma(amount: number): void {
    this.trauma = Math.min(1, this.trauma + amount);
  }
}

/**
 * Whether the camera may shake this frame: VFX at Full, no reduce-motion preference (read live
 * from the class `applyA11yPrefs` sets), not capture mode, not an automated browser (the visual
 * lane's frames must repeat). Hit stop is not gated: it moves no camera.
 */
export function shakeAllowed(captureMode: boolean): boolean {
  if (captureMode || vfxLevel() !== 'full') return false;
  if (typeof navigator !== 'undefined' && navigator.webdriver === true) return false;
  if (typeof document !== 'undefined') {
    if (document.documentElement.classList.contains('pxl-reduce-motion')) return false;
  }
  return true;
}
